import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Fuel, Layers, Network, ShieldCheck, Truck } from "lucide-react";
import { WhatsAppIcon } from "../../components/Brand";
import { waLink } from "../../lib/site";

const FEATURES = [
  [ShieldCheck, "Genuine Quality Products"],
  [Layers, "Bulk Supply Available"],
  [Fuel, "Modern Dispensing Facilities"],
  [Network, "Growing Distribution Network"],
];
const EASE = [0.16, 1, 0.3, 1];
const fade = (delay, y = 24) => ({ initial: { opacity: 0, y }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay, ease: EASE } });
const BTN = "group inline-flex w-full items-center justify-center gap-2.5 rounded-full px-7 py-4 font-semibold transition-[background-color,transform,box-shadow] hover:-translate-y-0.5 sm:w-auto";

const HeroCopy = () => (
  <div className="flex flex-col justify-center lg:col-span-6">
    <motion.p {...fade(0.1)} className="eyebrow flex items-center gap-3 text-brand">
      <span className="h-px w-10 bg-brand" /> MHM SUPER ADBLUE · Haldia, West Bengal
    </motion.p>
    <motion.h1 {...fade(0.2)} data-testid="hero-headline" className="mt-6 font-display font-extrabold uppercase leading-[0.95] tracking-tight text-[2.65rem] sm:text-6xl xl:text-[4.75rem]">
      <span className="block">Your trusted</span>
      <span className="block">Tata D.E.F. &amp; <span className="normal-case text-brand">AdBlue</span></span>
      <span className="block">Supply partner</span>
    </motion.h1>
    <motion.p {...fade(0.35)} data-testid="hero-description" className="mt-6 max-w-xl text-base leading-relaxed text-slate-200 md:text-lg">
      Reliable Diesel Exhaust Fluid (DEF/AdBlue) Supply for Trucks, Transporters, Fleet Operators &amp; Industrial Customers.
    </motion.p>
    <motion.div {...fade(0.5)} className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
      <Link to="/products" data-testid="hero-products-button" className={`${BTN} bg-brand text-white shadow-[0_18px_40px_-18px_rgba(255,107,0,0.9)] hover:bg-brand-dark`}>
        Explore Our Products <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </Link>
      <Link to="/bulk-orders" data-testid="hero-bulk-quote-button" className={`${BTN} bg-white text-navy hover:bg-slate-100`}>
        <Truck className="h-5 w-5 text-brand" /> Request Bulk Quote
      </Link>
      <a href={waLink()} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-button" className={`${BTN} bg-[#1FAF55] text-white hover:bg-[#178F45]`}>
        <WhatsAppIcon /> Contact Us on WhatsApp
      </a>
    </motion.div>
  </div>
);

const HeroPhoto = () => (
  <motion.div {...fade(0.25, 40)} className="relative lg:col-span-6">
    <div className="absolute -inset-3 rounded-[34px] bg-gradient-to-br from-brand/60 via-transparent to-[#3B82F6]/40 blur-2xl" />
    <figure className="relative overflow-hidden rounded-[28px] border border-white/20 bg-white/5 shadow-[0_50px_100px_-40px_rgba(0,0,0,0.8)]">
      <img src="/img/real/hero-shop5.jpg" alt="MHM SUPER ADBLUE Tata D.E.F storefront with dispensing facilities" data-testid="hero-shop-photo" className="aspect-square w-full object-cover" />
      <span className="absolute bottom-0 left-0 h-1.5 w-full bg-gradient-to-r from-brand via-brand to-[#FFB020]" />
    </figure>
  </motion.div>
);

const FeatureBar = () => (
  <motion.ul {...fade(0.65)} data-testid="hero-feature-bar" className="relative mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-[24px] bg-navy/10 shadow-[0_30px_70px_-30px_rgba(0,0,0,0.6)] lg:mt-16 lg:grid-cols-4">
    {FEATURES.map(([Icon, label], i) => (
      <li key={label} data-testid={`hero-feature-${i}`} className="group flex items-center gap-3 bg-white px-4 py-5 sm:gap-4 sm:px-6 sm:py-6">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-white sm:h-12 sm:w-12">
          <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
        </span>
        <span className="font-display text-base font-bold uppercase leading-tight text-navy sm:text-xl">{label}</span>
      </li>
    ))}
  </motion.ul>
);

export default function Hero() {
  return (
    <section data-testid="home-hero" className="relative overflow-hidden bg-gradient-to-br from-navy via-[#0E2A63] to-[#1E4FC9] text-white">
      <div className="absolute inset-0 grid-lines opacity-60" />
      <div className="pointer-events-none absolute -left-32 bottom-24 h-80 w-[140%] -rotate-6 rounded-[50%] border-t-[10px] border-brand/70" />
      <div className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-[#3B82F6]/25 blur-[140px]" />
      <div className="relative mx-auto max-w-7xl px-5 pb-14 pt-28 md:pt-36 lg:px-8 lg:pb-20">
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
          <HeroCopy />
          <HeroPhoto />
        </div>
        <FeatureBar />
      </div>
    </section>
  );
}
